import React, { useState, ChangeEvent, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import style from "./password-input.module.css";
import StyledButton from "./styled-button";
import { ClosedEyeIcon } from "frontend/icons/closed-eye-icon";
import { OpenedEyeIcon } from "frontend/icons/opened-eye-icon";
import { Lock } from "frontend/icons/lock";
import cn from "classnames";

interface BasePasswordInputRef {
  focus: () => void;
  resetValue: () => void;
}

interface BasePasswordInputProps {
  onPasswordSet: (password: string) => void;
  minLength?: number;
  renderValidationMessage: (hasError?: boolean, passwordLength?: number) => JSX.Element;
  alignCenter?: boolean;
  renderButton: (isValidPassword: boolean, handleAction: () => void, isFocused: boolean) => React.ReactNode;
  placeholder?: string;
}

const vIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="11" viewBox="0 0 12 11" fill="none">
    <path d="M1 4.88889L4.33333 8.77778L11 1" stroke="#1973FF" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const BasePasswordInput = forwardRef<BasePasswordInputRef, BasePasswordInputProps>(
  ({ onPasswordSet, minLength = 8, renderValidationMessage, alignCenter, renderButton }, ref) => {
    const [password, setPassword] = useState<string>("");
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isFocused, setIsFocused] = useState(false);
    const [debouncedHasError, setDebouncedHasError] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      resetValue: () => setPassword(""),
    }));

    const isValidPassword = password.length >= minLength;
    const hasError = password.length > 0 && !isValidPassword;

    useEffect(() => {
      setDebouncedHasError(false);

      // Set up the debounce timer
      const timer = setTimeout(() => {
        setPassword(password);
        setDebouncedHasError(hasError);
      }, 2000);

      // Cleanup the timer on each searchTerm change
      return () => clearTimeout(timer);
    }, [password]);

    function handlePasswordChange(e: ChangeEvent<HTMLInputElement>) {
      setPassword(e.target.value);
    }

    function togglePasswordVisibility() {
      setShowPassword(!showPassword);
    }

    function handleAction() {
      if (isValidPassword) {
        onPasswordSet(password);
      }
    }

    return (
      <div className={cn(style.passwordContainer, { [style.alignCenter]: alignCenter })}>
        <div className={style.inputWrapper}>
          <div className={style.inputGroup}>
            <Lock color={"#848199"} />
            <input
              ref={inputRef}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              placeholder={"Enter password"}
              className={style.passwordInput}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
            <button
              onClick={togglePasswordVisibility}
              className={style.visibilityToggle}
              type="button"
              style={{ width: 38 }}
            >
              {showPassword ? <OpenedEyeIcon /> : <ClosedEyeIcon />}
            </button>
          </div>
          {renderButton(isValidPassword, handleAction, isFocused)}
        </div>
        <div className={style.message}>{renderValidationMessage(debouncedHasError, password.length)}</div>
      </div>
    );
  }
);

BasePasswordInput.displayName = "BasePasswordInput";

export function PasswordInputSetter({
  isPasswordProtected = false,
  ...props
}: {
  isPasswordProtected?: boolean;
} & Omit<BasePasswordInputProps, "renderButton">) {
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [isCanvasPasswordProtected, setIsCanvasPasswordProtected] = useState(isPasswordProtected);
  const baseInputRef = useRef<BasePasswordInputRef>(null);

  useEffect(() => {
    if (passwordCopied) {
      const timer = setTimeout(() => {
        setPasswordCopied(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [passwordCopied]);

  function handleSetPassword(password: string) {
    setPasswordCopied(true);
    setPasswordSaved(true);
    navigator.clipboard.writeText(password);
    props.onPasswordSet(password);
  }

  function handleEditPassword() {
    setPasswordSaved(false);
    setIsCanvasPasswordProtected(false);
    baseInputRef.current?.resetValue();
    baseInputRef.current?.focus();
  }

  function renderButton(isValidPassword: boolean, handleAction: () => void, isFocused: boolean) {
    if (passwordCopied) {
      return (
        <div className={`${style.validationMessage} ${style.copied}`}>
          <span className={style.validationIcon}>{vIcon}</span>
          Password copied
        </div>
      );
    }
    const isEditing = isFocused || !(passwordSaved || isCanvasPasswordProtected);

    return (
      <StyledButton
        title={isEditing ? "Set Password" : "Edit Password"}
        onClick={isEditing ? () => handleAction() : handleEditPassword}
        enabled={isEditing ? isValidPassword : true}
        customStyle={{ height: 31, fontSize: 13, fontWeight: 500 }}
      />
    );
  }

  return (
    <BasePasswordInput ref={baseInputRef} {...props} renderButton={renderButton} onPasswordSet={handleSetPassword} />
  );
}

export function PasswordInputAccess({
  isLoading = false,
  ...props
}: {
  isLoading?: boolean;
} & Omit<BasePasswordInputProps, "renderButton">) {
  function renderButton(isValidPassword: boolean, handleAction: () => void) {
    return (
      <StyledButton
        title="Continue"
        onClick={handleAction}
        enabled={isValidPassword}
        customStyle={{ height: 31, fontSize: 13, fontWeight: 500 }}
        loading={isLoading}
      />
    );
  }

  return <BasePasswordInput {...props} renderButton={renderButton} />;
}

export default BasePasswordInput;
